import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import FastAPIClient from '../services/apiClient'; // Import the client
import logo_1024_X_1024 from '../assets/logo_1024_X_1024.png'
import { checkpointOrganizations } from '../apiHelper';
import { Building2 } from 'lucide-react';
import Modal from '../components/ui/Modal';

/**
 * Login Page for the application.
 * Allows users to log in with their username (email) and password.
 * Handles client-side validation and interacts with the FastAPI backend via apiClient.
 * Designed with Tailwind CSS, adhering to the specified design principles.
 */

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation(); // To access state passed from signup page
    const client = new FastAPIClient(); // Instantiate the client

    const [formData, setFormData] = useState({
        username: location.state?.username || '', // Pre-fill username if passed from signup
        password: '',
    });
    
    const [errors, setErrors] = useState({});
    const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
    const [successMessage, setSuccessMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [isOrganizationModalOpen, setIsOrganizationModalOpen] = useState(false);
    const [selectedOrgId, setSelectedOrgId] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        // Clear error for the field as user types
        if (errors[name]) {
            setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
        }
    };

    const validateForm = () => {
        let newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.username) {
            newErrors.username = 'Email is required.';
        } else if (!emailRegex.test(formData.username)) {
            newErrors.username = 'Invalid email format.';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('loading');
        setErrors({}); // Clear previous errors
        setSuccessMessage('');

        if (!validateForm()) {
            setSubmitStatus('error');
            setErrors(prev => ({ ...prev, general: 'Please correct the errors in the form.' }));
            return;
        }

        try {
            const orgsResponse = await checkpointOrganizations(formData.username);
            if (orgsResponse && orgsResponse.length > 1) {
                setOrganizations(orgsResponse);
                setIsOrganizationModalOpen(true);
                setSubmitStatus('idle');
                return;
            } else if (orgsResponse && orgsResponse == 1) {
                setSelectedOrgId(orgsResponse[0].organization_id);
            }
            // if 0 or 1 org, proceed to actual login
            await performLogin(orgsResponse[0]?.organization_id);
        } catch (error) {
            handleLoginError(error);
        }
    };

    const performLogin = async (orgId = null) => {
        setSubmitStatus('loading')
        try {
            if (orgId) {
                localStorage.setItem('org_id', orgId)
            }
            // Call the login method from FastAPIClient
            const user = await client.login(
                formData.username,
                formData.password,
            );

            // console.log('Login successful. User:', user);
            setSubmitStatus('success');
            setSuccessMessage(user.data.msg);
            setTimeout(() => {
                const lcUser = localStorage.getItem("user")
                navigate("/login-verification", { state: { ...JSON.parse(lcUser) } });
            }, 1500);
        } catch (error) {
            localStorage.removeItem('org_id');
            handleLoginError(error);
        }
    };

    const handleLoginError = (error) => {
        console.error('Login error:', error);
        setSubmitStatus('error');
        // Check if error.response.data exists and has a 'detail' or 'message' field
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'An unexpected error occurred during login.';
        setErrors(prev => ({ ...prev, general: errorMessage }));
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-light p-4">
            <Card className="w-full max-w-md mx-auto p-8">
                {/* Logo strategically placed at the top of the card */}
                <div className="flex justify-center mb-6">
                    <img
                        src={logo_1024_X_1024}
                        alt="Arxis Logo"
                        className="w-24 h-24 object-contain rounded-lg select-none pointer-events-none" // Added select-none and pointer-events-none for protection
                        onContextMenu={(e) => e.preventDefault()} // Prevent right-click context menu
                        draggable="false" // Prevent dragging the image
                    />
                </div>

                <h2 className="text-2xl font-bold text-center text-text-primary mb-6">
                    Welcome Back to Arxis
                </h2>
                <p className="text-center text-text-secondary mb-6 text-sm">
                    Log in to manage your accounting and inventory.
                </p>

                <form onSubmit={handleSubmit} noValidate>
                    <Input
                        label="Email Address"
                        id="username"
                        name="username"
                        type="email"
                        placeholder="e.g., yourbusiness@example.com"
                        value={formData.username}
                        onChange={handleChange}
                        required
                        error={errors.username}
                    />
                    {errors.username && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors.username}</p>}

                    <Input
                        label="Password"
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Your password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        error={errors.password}
                        trailingIcon={
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none pointer-events-auto"
                                aria-label={showPassword ? "Hide Password" : "Show Password"}
                            >
                                {showPassword ? (
                                    // Eye slashed (hidden)
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                        <path d="M1 1l22 22" />
                                    </svg>
                                ) : (
                                    // Eye open (visible)
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                )}
                            </button>
                        }
                    />
                    {errors.password && <p className="text-gmail-red text-xs mt-[-10px] mb-4">{errors.password}</p>}

                    {submitStatus === 'error' && errors.general && (
                        <p className="text-gmail-red text-sm text-center mb-4">{errors.general}</p>
                    )}
                    {submitStatus === 'success' && successMessage && (
                        <p className="text-gmail-green text-sm text-center mb-4">{successMessage}</p>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        disabled={submitStatus === 'loading'}
                        className="mt-4"
                    >
                        {submitStatus === 'loading' ? 'Logging In...' : 'Login'}
                    </Button>
                </form>

                <p className="text-center text-sm text-text-secondary mt-6">
                    <Link to="/forgot-password"
                        className="text-gmail-blue hover:underline focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gmail-blue rounded-sm"
                    >
                        Forgot password?
                    </Link>
                    {/* Don't have an account?{' '} */}
                    {/* <Link to="/signup-business" className="text-gmail-blue hover:underline focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gmail-blue rounded-sm">
                        Register your business
                    </Link> */}
                </p>
            </Card>
            <Modal
                isOpen={isOrganizationModalOpen}
                onClose={() => setIsOrganizationModalOpen(false)}
                title="Select Organization"
                size="md"
            >
                <p className="text-sm text-text-secondary mb-4">
                    We found multiple accounts associated with this email. Please select the organization you wish to access:
                </p>
                <div className="space-y-3">
                    {organizations.map((org) => (
                        <button
                            key={org.organization_id}
                            onClick={() => {
                                setIsOrganizationModalOpen(false);
                                setSelectedOrgId(org.organization_id);
                                performLogin(org.organization_id);
                            }}
                            className="w-full flex items-center p-4 border border-gray-200 rounded-lg hover:border-gmail-blue hover:bg-blue-50 transition-all group text-left"
                        >
                            <div className="bg-gray-100 p-3 rounded-full group-hover:bg-white transition-colors mr-4">
                                <Building2 size={24} className="text-gray-600 group-hover:text-gmail-blue" />
                            </div>
                            <div className="flex-1">
                                <h4 className="font-bold text-text-primary">{org.organization_name}</h4>
                                <p className="text-xs text-text-secondary uppercase tracking-wider">
                                    Category: {org.business_category}
                                </p>
                            </div>
                            <div className="text-gray-300 group-hover:text-gmail-blue">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                            </div>
                        </button>
                    ))}
                </div>
            </Modal>
        </div>
    );
};

export default LoginPage;